import React, { useState } from 'react';
import { X, PlusCircle, Link2 } from 'lucide-react';
import { HorseStatus, HorseRead, PrivateHorseCreateRequest, PrivateHorseLinkRequest, UserHorseCollectionRead } from '../types/api';
import { apiService } from '../services/api';
import { HorseSelector } from './HorseSelector';

interface ModalShellProps {
  isOpen: boolean;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}

const ModalShell: React.FC<ModalShellProps> = ({ isOpen, title, onClose, children }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center px-4 py-6 sm:py-10 overflow-y-auto">
      <div className="absolute inset-0 bg-black opacity-40" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-white rounded-lg shadow-xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-700"
            aria-label="Close modal"
          >
            <X size={18} />
          </button>
        </div>
        <div className="p-6 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
};

// --- Create Private Horse Modal ---

interface CreatePrivateHorseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (entry: UserHorseCollectionRead) => void;
}

type HorseFieldKeys =
  | 'name_equipe'
  | 'name_ht'
  | 'breed_equipe'
  | 'color_equipe'
  | 'sex_equipe'
  | 'born_year_equipe'
  | 'height_equipe'
  | 'sire_equipe'
  | 'dam_equipe'
  | 'breeder_equipe'
  | 'owner_equipe'
  | 'temperament'
  | 'description';

const defaultHorseFields: Record<HorseFieldKeys, string> = {
  name_equipe: '',
  name_ht: '',
  breed_equipe: '',
  color_equipe: '',
  sex_equipe: '',
  born_year_equipe: '',
  height_equipe: '',
  sire_equipe: '',
  dam_equipe: '',
  breeder_equipe: '',
  owner_equipe: '',
  temperament: '',
  description: '',
};

const statusOptions: HorseStatus[] = ['watch', 'skip', 'own', 'sold'];

export const CreatePrivateHorseModal: React.FC<CreatePrivateHorseModalProps> = ({
  isOpen,
  onClose,
  onCreated,
}) => {
  const [horseFields, setHorseFields] = useState<Record<HorseFieldKeys, string>>(defaultHorseFields);
  const [privateName, setPrivateName] = useState('');
  const [collectionStatus, setCollectionStatus] = useState<HorseStatus>('own');
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetForm = () => {
    setHorseFields(defaultHorseFields);
    setPrivateName('');
    setCollectionStatus('own');
    setNotificationsEnabled(true);
    setError(null);
  };

  const handleFieldChange = (field: HorseFieldKeys, value: string) => {
    setHorseFields(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const buildRequestPayload = (): PrivateHorseCreateRequest => {
    const {
      name_equipe,
      name_ht,
      breed_equipe,
      color_equipe,
      sex_equipe,
      born_year_equipe,
      height_equipe,
      sire_equipe,
      dam_equipe,
      breeder_equipe,
      owner_equipe,
      temperament,
      description,
    } = horseFields;

    if (!name_equipe.trim()) {
      throw new Error('Horse name is required');
    }

    const payload: PrivateHorseCreateRequest = {
      horse: {
        name_equipe: name_equipe.trim(),
        name_ht: name_ht?.trim() || undefined,
        breed_equipe: breed_equipe?.trim() || undefined,
        color_equipe: color_equipe?.trim() || undefined,
        sex_equipe: sex_equipe || undefined,
        born_year_equipe: born_year_equipe ? Number(born_year_equipe) : undefined,
        height_equipe: height_equipe ? Number(height_equipe) : undefined,
        sire_equipe: sire_equipe?.trim() || undefined,
        dam_equipe: dam_equipe?.trim() || undefined,
        breeder_equipe: breeder_equipe?.trim() || undefined,
        owner_equipe: owner_equipe?.trim() || undefined,
        temperament: temperament?.trim() || undefined,
        description: description?.trim() || undefined,
      },
      collection: {
        status: collectionStatus,
        notification_enabled: notificationsEnabled,
        private_name: privateName?.trim() || undefined,
      },
    };

    return payload;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    try {
      const payload = buildRequestPayload();
      setIsSubmitting(true);

      const response = await apiService.createPrivateHorse(payload);
      onCreated(response);
      resetForm();
      onClose();
    } catch (err) {
      console.error('Failed to create private horse', err);
      setError(err instanceof Error ? err.message : 'Failed to create private horse');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalShell isOpen={isOpen} onClose={onClose} title="Add Private Horse">
      <form className="space-y-6" onSubmit={handleSubmit}>
        {error && (
          <div className="p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Horse Name *
            </label>
            <input
              type="text"
              value={horseFields.name_equipe}
              onChange={(e) => handleFieldChange('name_equipe', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g., Starfire"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Barn / Nickname
            </label>
            <input
              type="text"
              value={horseFields.name_ht}
              onChange={(e) => handleFieldChange('name_ht', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g., Bella"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Breed
            </label>
            <input
              type="text"
              value={horseFields.breed_equipe}
              onChange={(e) => handleFieldChange('breed_equipe', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g., Hanoverian"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sex
            </label>
            <select
              value={horseFields.sex_equipe}
              onChange={(e) => handleFieldChange('sex_equipe', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select</option>
              <option value="Stallion">Stallion</option>
              <option value="Mare">Mare</option>
              <option value="Gelding">Gelding</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Birth Year
            </label>
            <input
              type="number"
              value={horseFields.born_year_equipe}
              onChange={(e) => handleFieldChange('born_year_equipe', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              min="1900"
              max={new Date().getFullYear()}
              placeholder="2015"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Height (cm)
            </label>
            <input
              type="number"
              value={horseFields.height_equipe}
              onChange={(e) => handleFieldChange('height_equipe', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              min="0"
              step="1"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sire
            </label>
            <input
              type="text"
              value={horseFields.sire_equipe}
              onChange={(e) => handleFieldChange('sire_equipe', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Dam
            </label>
            <input
              type="text"
              value={horseFields.dam_equipe}
              onChange={(e) => handleFieldChange('dam_equipe', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Temperament
            </label>
            <textarea
              value={horseFields.temperament}
              onChange={(e) => handleFieldChange('temperament', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="Short description of demeanor..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={horseFields.description}
              onChange={(e) => handleFieldChange('description', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="Training history, show record, notes..."
            />
          </div>
        </div>

        <div className="border-t border-gray-200 pt-4 space-y-4">
          <h4 className="text-sm font-semibold text-gray-800 uppercase tracking-wide">
            Collection Options
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Collection Status
              </label>
              <select
                value={collectionStatus}
                onChange={(e) => setCollectionStatus(e.target.value as HorseStatus)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                {statusOptions.map(status => (
                  <option key={status} value={status}>
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Private Name
              </label>
              <input
                type="text"
                value={privateName}
                onChange={(e) => setPrivateName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="Optional alias"
              />
            </div>
          </div>

          <label className="inline-flex items-center text-sm text-gray-700">
            <input
              type="checkbox"
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              checked={notificationsEnabled}
              onChange={(e) => setNotificationsEnabled(e.target.checked)}
            />
            <span className="ml-2">Enable notifications for this horse</span>
          </label>
        </div>

        <div className="flex justify-end space-x-3 pt-2">
          <button
            type="button"
            onClick={() => {
              resetForm();
              onClose();
            }}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            <PlusCircle size={16} className="mr-2" />
            {isSubmitting ? 'Saving...' : 'Create Private Horse'}
          </button>
        </div>
      </form>
    </ModalShell>
  );
};

// --- Link Private Horse Modal ---

interface LinkPrivateHorseModalProps {
  isOpen: boolean;
  privateHorseId: string;
  onClose: () => void;
  onLinked: (entry: UserHorseCollectionRead, targetHorseId: string) => void;
}

export const LinkPrivateHorseModal: React.FC<LinkPrivateHorseModalProps> = ({
  isOpen,
  privateHorseId,
  onClose,
  onLinked,
}) => {
  const [selectedHorse, setSelectedHorse] = useState<HorseRead | null>(null);
  const [includeSummaryNote, setIncludeSummaryNote] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!selectedHorse) {
      setError('Please select a horse to link to.');
      return;
    }

    const payload: PrivateHorseLinkRequest = {
      target_horse_id: selectedHorse.unified_horse_key,
      include_summary_note: includeSummaryNote,
    };

    try {
      setIsSubmitting(true);
      const result = await apiService.linkPrivateHorse(privateHorseId, payload);
      onLinked(result, payload.target_horse_id);
      setSelectedHorse(null);
      setIncludeSummaryNote(true);
      onClose();
    } catch (err) {
      console.error('Failed to link private horse', err);
      setError(err instanceof Error ? err.message : 'Failed to link private horse');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalShell isOpen={isOpen} onClose={onClose} title="Link to Public Horse">
      <form className="space-y-4" onSubmit={handleSubmit}>
        {error && (
          <div className="p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700">
            {error}
          </div>
        )}

        <HorseSelector
          onSelect={(horse) => setSelectedHorse(horse)}
          selectedHorseId={selectedHorse?.unified_horse_key}
          helperText="Search across the public catalog and pick the horse you want to link."
        />

        <label className="inline-flex items-center text-sm text-gray-700">
          <input
            type="checkbox"
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            checked={includeSummaryNote}
            onChange={(e) => setIncludeSummaryNote(e.target.checked)}
          />
          <span className="ml-2">Create a summary note on the linked horse</span>
        </label>

        <div className="flex justify-end space-x-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            <Link2 size={16} className="mr-2" />
            {isSubmitting ? 'Linking...' : 'Link Horse'}
          </button>
        </div>
      </form>
    </ModalShell>
  );
};

